import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Typography from '@mui/material/Typography';
import TuneIcon from '@mui/icons-material/Tune';
import Box from '@mui/material/Box';

import AdditionalSettingsModal from 'pages/additionalSettings/modal';
import {
    onOpenRightSidebarAutomatically,
    setSelectedOutputLanguages,
    setStreamingValues,
    setJobOrderData,
    clearJobOrderData,
} from 'store/global/globalSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { JobParamsType } from 'store/auth/types';
import { getFormattedFilters, getTranslatedDataLanguages } from 'helpers/filters';
import { useTranslation } from 'react-i18next';
import { reqGetJob, reqGetOrder } from 'api/requests/main';
import {
    IntakeNotesType,
    JobOrderDisplayType,
    JobOrderRawType,
    JobOrderRegenerateBody,
} from './types';
import { v4 as uuid } from 'uuid';
import { logEventFirebase, setFirestoreDoc } from 'utils/config/firebase';
import { Checkbox, FormControlLabel, FormGroup, Switch, Tooltip } from '@mui/material';
import { showErrorMessages } from 'helpers/error';
import { deleteHtmlFromString } from 'helpers/htmlParser';
import { SelectedLanguageType } from 'store/global/types';
import { filterLanguagesByCountry, formatLanguages } from 'helpers/languages';
import Signature from 'components/Signature';
import {
    ADDITIONAL_SETTINGS_INSTRUCTION,
    POST_DEFAULT_LANGUAGES,
} from 'utils/config/constants/controllerNames';
import { getDefaultLanguagesForJob } from 'api/queries/languages';
import { JobLanguagesType } from 'components/DefaultSettings/types';
import blueGenerateIcon from 'assets/images/icons/generate-blue.svg';
import whiteGenerateIcon from 'assets/images/icons/generate-white.svg';
import { Storage } from 'storage';
import { useSectionsFeatureFlag } from 'hooks/useSectionsFeatureFlag';
import ROneSwitch from 'components/ROneSwitch';
import ManualInput from './ManualInput';
import InfoBox from './InfoBox';
import {
    transformToDisplayData,
    parseJobOrderRaw,
    createRegenerateRequestBody,
} from './jobOrderUtils';
import { FileUpload } from 'components/FileUpload';
import { UploadedFile } from 'components/UploadedFile';
import { AxiosError } from 'axios';
import { biasCheckFail } from '../../helpers/messages';

// Default values for job order display data
const DEFAULT_JOB_ORDER_DISPLAY: JobOrderDisplayType = {
    job_title: null,
    country: null,
    brand: null,
    language: null,
    description: null,
    responsibilities: null,
    qualifications: null,
    remote_work_allowed: null,
    employment_categories: null,
    expected_hours_of_service: null,
    work_locations: null,
    pay_rates: null,
    education_requirements: null,
    reason_for_opening: null,
    job_requirements: null,
    job_level: null,
};

const IntakeNotes: FC<IntakeNotesType> = ({
    clearStreamingAfterGenerating,
    setIsGeneratingJob,
    isGeneratingJob,
    defaultValue,
    setShowSkeleton,
    selectedDocumentId,
    existingMessages,
    firstVersion,
    getFilters,
    jobOrderId,
    attachedFile,
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { document_id = '' } = useParams();
    const { userSettings } = useAppSelector((state) => state.auth);
    const {
        additionalSettings,
        selectedOutputLanguages,
        outputLanguages,
        isSignatureChecked,
        jobOrderData,
    } = useAppSelector((state) => state.global);
    const [isGenerateHovered, setIsGenerateHovered] = useState<boolean>(false);
    const [isGeneratingOrder, setIsGeneratingOrder] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showEditorToolbar, setShowEditorToolbar] = useState<boolean>(false);
    const [value, setValue] = useState<string | undefined>(defaultValue);
    const [savedLanguages, setSavedLanguages] = useState<JobLanguagesType | null>(null);
    const [jobOrderDisplay, setJobOrderDisplay] =
        useState<JobOrderDisplayType>(DEFAULT_JOB_ORDER_DISPLAY);
    const [countryOutputLanguages, setCountryOutputLanguages] = useState<SelectedLanguageType[]>(
        [],
    );
    const [files, setFiles] = useState<FileList | null>(null);
    const prevDocumentId = useRef<string | undefined>(undefined);

    const { isSectionsModeEnabled } = useSectionsFeatureFlag();

    const isAfterGeneration = !!document_id;
    const isGeneratingNewConversation = !document_id && isLoading;
    const notValue = value ? !deleteHtmlFromString(value).trim() && isLoading : true;
    const noCheckedLanguages =
        countryOutputLanguages.filter((l) => l.checked === true).length === 0;
    const isROneOrFile = jobOrderId || (files && files?.length > 0) ? false : notValue;
    const generateDisabled = noCheckedLanguages || isGeneratingJob || isROneOrFile;
    const [_generatingGroupId, setGeneratingGroupId] = useState<string | undefined>(undefined);

    const getLanguages = () => {
        if (!outputLanguages?.length) {
            return [];
        }
        const languages = getTranslatedDataLanguages(outputLanguages);
        if (savedLanguages) {
            const languagesPerCountry = languages?.filter(
                (item) => item.country_code === savedLanguages.target_country_code,
            );
            const formattedLanguage = formatLanguages(
                languagesPerCountry,
                savedLanguages.languages,
            );
            const checkedItems = formattedLanguage?.filter((item) => item.checked);
            dispatch(setSelectedOutputLanguages(checkedItems));
            return setCountryOutputLanguages(formattedLanguage);
        }

        if (firstVersion.targetCountry) {
            const languagesPerCountry = languages?.filter(
                (item) => item.country === firstVersion.targetCountry,
            );
            const formattedLanguage = formatLanguages(languagesPerCountry, [
                firstVersion?.language,
            ]);
            const checkedItems = formattedLanguage?.filter((item) => item.checked);
            dispatch(setSelectedOutputLanguages(checkedItems));
            return setCountryOutputLanguages(formattedLanguage);
        }

        if (userSettings) {
            const formattedLanguage = filterLanguagesByCountry(
                userSettings.target_country,
                languages,
                userSettings.output_language_code,
            );
            const checkedItems = formattedLanguage?.filter((item) => item.checked);
            dispatch(setSelectedOutputLanguages(checkedItems));

            setCountryOutputLanguages(formattedLanguage);
        }
    };

    // Reset editor value when switching between documents to allow new defaultValue to load
    useEffect(() => {
        if (prevDocumentId.current && document_id && prevDocumentId.current !== document_id) {
            setValue(undefined);
        }
        prevDocumentId.current = document_id;
    }, [document_id]);

    // Initialize editor with defaultValue if it hasn't been edited yet (value is undefined)
    useEffect(() => {
        if (defaultValue !== undefined && value === undefined) {
            setValue(defaultValue);
        }
    }, [defaultValue, value]);

    useEffect(() => {
        if (!document_id) {
            setSavedLanguages(null);
            // Clear job order data when not in a specific document context
            dispatch(clearJobOrderData());
        } else {
            getDefaultLanguagesForJob(document_id, setSavedLanguages);
        }
    }, [document_id, dispatch]);

    useEffect(() => {
        async function getOrder() {
            try {
                if (jobOrderId) {
                    setIsGeneratingOrder(true);
                    const res = await reqGetOrder(jobOrderId);
                    const data = res.data;
                    const jobOrder: JobOrderRawType = parseJobOrderRaw(data, jobOrderId);
                    dispatch(setJobOrderData(jobOrder));
                    setJobOrderDisplay(transformToDisplayData(jobOrder));
                }
            } catch (err) {
                showErrorMessages(err);
            } finally {
                setIsGeneratingOrder(false);
            }
        }
        getOrder();
    }, [dispatch, jobOrderId]);

    useEffect(() => {
        getLanguages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outputLanguages, userSettings, savedLanguages]);

    const handleEditorChange = (value: string) => {
        setValue(value);
    };

    const handleCheckboxChange = (languageCode: string, checked: boolean) => {
        setCountryOutputLanguages((prev) => {
            const newState = prev.map((language) => ({
                ...language,
                checked: language.language_code === languageCode ? checked : language.checked,
            }));
            const selectedLanguages = newState.filter((l) => l.checked === true);

            dispatch(setSelectedOutputLanguages(selectedLanguages));
            return newState;
        });
    };

    const validateJobGenerationData = (
        intakeNotes: string | undefined,
        jobOrderData: JobOrderRawType | null,
    ): boolean => {
        const intakeNotesString = intakeNotes ?? '';
        const hasFiles = files && files?.length > 0;
        const hasIntakeNotes = jobOrderData?.title
            ? true
            : intakeNotes && deleteHtmlFromString(intakeNotesString).trim();
        const hasJobOrderData =
            jobOrderData &&
            Object.values(jobOrderData).some(
                (v) => (typeof v === 'string' && v.trim()) || (Array.isArray(v) && v.length > 0),
            );

        return !!(hasIntakeNotes || hasJobOrderData || hasFiles);
    };

    const onSubmit = async (params?: JobParamsType, updateFilters?: (doc_id: string) => void) => {
        try {
            if (!validateJobGenerationData(value, jobOrderData)) {
                console.warn('No intake notes or job order data available for generation');
                return;
            }

            setIsLoading(true);
            setIsGeneratingJob(true);
            existingMessages.current = [];

            const conversation_id = uuid();
            if (updateFilters) {
                updateFilters(conversation_id);
            }
            setShowSkeleton(selectedOutputLanguages?.map((item) => item.language_code));
            const savedInstruction = Storage.getItem('additional_settings_instruction');

            for (const language of selectedOutputLanguages) {
                const parameters =
                    params ||
                    getFormattedFilters(
                        getFilters(),
                        additionalSettings,
                        userSettings?.target_country_code,
                    );
                if (!parameters?.instructions && !document_id) {
                    if (savedInstruction && parameters) {
                        parameters.instructions = savedInstruction;
                    }
                    setFirestoreDoc(ADDITIONAL_SETTINGS_INSTRUCTION, conversation_id, {
                        value: savedInstruction,
                    });
                }
                Storage.removeItem('additional_settings_instruction');

                // Prepare the request body for the application backend API
                const requestBody: JobOrderRegenerateBody = createRegenerateRequestBody(
                    conversation_id,
                    undefined,
                    language,
                    jobOrderData,
                    userSettings?.brand || jobOrderData?.brand || '',
                    value ?? '',
                    parameters,
                    isSignatureChecked,
                    isSectionsModeEnabled || jobOrderData?.r1_flag || false,
                );

                // Build final request data: FormData if file attached, otherwise JSON
                let finalRequestBody: JobOrderRegenerateBody | FormData = requestBody;
                if (files && files.length > 0) {
                    const formData = new FormData();
                    // Append the entire request body as a JSON string in the 'data' field
                    formData.append('data', JSON.stringify(requestBody));
                    // Append the file in the 'file' field
                    formData.append('file', files[0]);
                    finalRequestBody = formData;
                }

                const response = await reqGetJob<typeof finalRequestBody>(finalRequestBody);
                const { message_id, content } = await response.data;
                dispatch(
                    setStreamingValues({
                        jobId: message_id,
                        value: content,
                        done: true,
                    }),
                );
                clearStreamingAfterGenerating(message_id);
                setIsLoading(false);
            }

            logEventFirebase('click_generate_job');
            dispatch(onOpenRightSidebarAutomatically());
            setFirestoreDoc(POST_DEFAULT_LANGUAGES, conversation_id, {
                languages: selectedOutputLanguages?.map((item) => item.language_code),
                target_country_code: userSettings?.target_country_code,
                target_country: userSettings?.target_country,
                brand: userSettings?.brand,
            });
            navigate(`/job/${conversation_id}`);
            selectedDocumentId.current = conversation_id;
            if (userSettings) {
                setCountryOutputLanguages(
                    filterLanguagesByCountry(
                        userSettings.target_country,
                        outputLanguages,
                        userSettings.output_language_code,
                    ),
                );
            }
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 422) {
                const detail = error.response.data?.detail;
                if (typeof detail === 'object' && detail?.error === 'bias_detected') {
                    biasCheckFail();
                    setIsGeneratingJob(false);
                    setGeneratingGroupId(undefined);
                    return;
                }
            }
            showErrorMessages(error, 'handleRewrite');
            setIsGeneratingJob(false);
            setGeneratingGroupId(undefined);
        }
    };

    const onRegenerate = async (params?: JobParamsType) => {
        try {
            if (!validateJobGenerationData(value, jobOrderData)) {
                console.warn('No intake notes or job order data available for regeneration');
                return;
            }

            setIsLoading(true);
            setIsGeneratingJob(true);
            existingMessages.current = [];

            setShowSkeleton(selectedOutputLanguages?.map((item) => item.language_code));
            const savedInstruction = Storage.getItem('additional_settings_instruction');

            const selectedCountry = savedLanguages?.target_country ?? firstVersion?.targetCountry;
            const brand =
                userSettings?.target_country === selectedCountry
                    ? userSettings?.brand
                    : (savedLanguages?.brand ?? firstVersion?.brand);

            for (const language of selectedOutputLanguages) {
                const parameters =
                    params ||
                    getFormattedFilters(
                        getFilters(),
                        additionalSettings,
                        userSettings?.target_country_code,
                    );
                if (!parameters?.instructions && !document_id) {
                    if (savedInstruction && parameters) {
                        parameters.instructions = savedInstruction;
                    }
                    setFirestoreDoc(ADDITIONAL_SETTINGS_INSTRUCTION, document_id, {
                        value: savedInstruction,
                    });
                }
                Storage.removeItem('additional_settings_instruction');
                const requestBody: JobOrderRegenerateBody = createRegenerateRequestBody(
                    document_id,
                    undefined,
                    language,
                    jobOrderData,
                    brand,
                    value ?? '',
                    parameters,
                    isSignatureChecked,
                    isSectionsModeEnabled || jobOrderData?.r1_flag || false,
                );
                const response = await reqGetJob<typeof requestBody>(requestBody);
                const { message_id, content } = await response.data;
                dispatch(
                    setStreamingValues({
                        jobId: message_id,
                        value: content,
                        done: true,
                    }),
                );
                clearStreamingAfterGenerating(message_id);
            }

            setIsLoading(false);
            setIsGeneratingJob(false);
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 422) {
                const detail = error.response.data?.detail;
                if (typeof detail === 'object' && detail?.error === 'bias_detected') {
                    biasCheckFail();
                    setIsGeneratingJob(false);
                    setGeneratingGroupId(undefined);
                    return;
                }
            }
            setIsLoading(false);
            setIsGeneratingJob(false);
            setShowSkeleton([]);
            console.log(error);
        }
    };

    const submitFunction = isAfterGeneration ? onRegenerate : onSubmit;

    return (
        <>
            <Box
                className={`intake-notes-editor ${
                    isAfterGeneration ? 'floating-intake-notes' : 'editor-full-screen'
                } ${isGeneratingNewConversation ? 'loading' : ''}`}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    maxHeight: '100vh',
                    overflow: 'auto',
                }}
            >
                {/* Job Order Info */}
                {jobOrderId && (
                    <Box sx={{ marginBottom: 2 }}>
                        <InfoBox
                            jobOrderDisplayData={jobOrderDisplay}
                            isNarrow={isGeneratingNewConversation || isAfterGeneration}
                        />
                    </Box>
                )}
                <Box className="title">
                    <Box className="title-with-signature">
                        <Typography variant="h3">{t('web_app.home.intake_notes.title')}</Typography>
                        {!isGeneratingNewConversation && !isAfterGeneration && (
                            <Box className="intake-notes-actions">
                                <Tooltip
                                    placement="bottom"
                                    title={t('web_app.intake_notes.toolbar_toggle_tooltip')}
                                >
                                    <Box className="intake-notes-actions-layout">
                                        {t('web_app.intake_notes.toolbar_toggle')}
                                        <Switch
                                            checked={showEditorToolbar}
                                            onChange={(event) =>
                                                setShowEditorToolbar(event.target.checked)
                                            }
                                        />
                                    </Box>
                                </Tooltip>
                                <Signature disabled={isGeneratingJob} />
                                <ROneSwitch isGeneratingJob={isGeneratingJob} />
                            </Box>
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        marginBottom: 1,
                        height: isGeneratingNewConversation || isAfterGeneration ? '100%' : 'auto',
                    }}
                >
                    <ManualInput
                        value={value ?? ''}
                        onChange={handleEditorChange}
                        readOnly={isLoading}
                        showToolbar={isAfterGeneration ? true : showEditorToolbar}
                        isGenerating={isGeneratingJob}
                        isLoading={isLoading}
                        isGeneratingOrder={isGeneratingOrder}
                        notValue={notValue}
                    />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        marginBottom: 1,
                    }}
                >
                    {!document_id && !isGeneratingJob && (
                        <FileUpload setFiles={setFiles} files={files} disabled={isGeneratingJob} />
                    )}
                    {attachedFile?.url && attachedFile?.filename && (
                        <UploadedFile url={attachedFile.url} filename={attachedFile.filename} />
                    )}
                </Box>

                <Box className="intake-notes-editor-actions flex" sx={{ flexShrink: 0 }}>
                    <FormGroup className="languages-checkboxes">
                        <Typography>{t('web_app.intake_notes.output_languages')}</Typography>
                        {getTranslatedDataLanguages(countryOutputLanguages).map((language) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={language.checked}
                                        onChange={() =>
                                            handleCheckboxChange(
                                                language.language_code,
                                                !language.checked,
                                            )
                                        }
                                        value={language.id}
                                        key={language.id}
                                        disabled={isGeneratingJob}
                                    />
                                }
                                label={
                                    defaultValue || isLoading
                                        ? language?.language_code?.toUpperCase()
                                        : language.language_name
                                }
                                key={language.id}
                            />
                        ))}
                    </FormGroup>
                    {(isAfterGeneration || isGeneratingNewConversation) && (
                        <>
                            <Signature disabled={isGeneratingJob} />
                            <ROneSwitch isGeneratingJob={isGeneratingJob} />
                            <hr />
                        </>
                    )}
                    <Box
                        className={`action-buttons ${
                            generateDisabled ? 'action-buttons-disable' : ''
                        }`}
                    >
                        <Tooltip placement="top" title={t('web_app.tooltips.advance_settings_btn')}>
                            <span>
                                <button
                                    className="action-buttons-settings"
                                    onClick={() => setIsModalOpen(true)}
                                    disabled={generateDisabled}
                                >
                                    <TuneIcon
                                        className={
                                            isGenerateHovered
                                                ? 'animate__animated animate__bounceIn'
                                                : ''
                                        }
                                        sx={{ fontSize: 24, color: '#000' }}
                                    />
                                </button>
                            </span>
                        </Tooltip>
                        <Tooltip placement="top" title={t('web_app.tooltips.generate_btn')}>
                            <span>
                                <button
                                    onMouseEnter={() => setIsGenerateHovered(true)}
                                    onMouseLeave={() => setIsGenerateHovered(false)}
                                    className="action-buttons-main int-nots-btn"
                                    onClick={() => submitFunction()}
                                    disabled={generateDisabled}
                                >
                                    {generateDisabled ? (
                                        <img src={whiteGenerateIcon} alt="generate icon" />
                                    ) : (
                                        <img src={blueGenerateIcon} alt="generate icon" />
                                    )}
                                    {t('web_app.home.intake_notes.generate')}
                                </button>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
            </Box>
            <AdditionalSettingsModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onGenerate={submitFunction}
            />
        </>
    );
};

export default IntakeNotes;
